import React, { useState } from 'react';
import { fruits, weapons } from '../data/gameData';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ComparisonTools = ({ darkMode }) => {
  const [selectedItems, setSelectedItems] = useState({
    fruits: [],
    weapons: []
  });
  
  const [comparisonType, setComparisonType] = useState('fruits');
  
  const handleItemSelect = (id, type) => {
    setSelectedItems(prev => {
      const currentItems = [...prev[type]];
      const exists = currentItems.includes(id);
      
      if (exists) {
        return {
          ...prev,
          [type]: currentItems.filter(itemId => itemId !== id)
        };
      } else if (currentItems.length < 3) {
        return {
          ...prev,
          [type]: [...currentItems, id]
        };
      }
      
      return prev;
    });
  };
  
  const getComparisonData = () => {
    if (comparisonType === 'fruits') {
      const selectedFruits = fruits.filter(fruit => 
        selectedItems.fruits.includes(fruit.id)
      );
      
      return selectedFruits.map(fruit => ({
        name: fruit.name,
        pvp: fruit.pvpRating,
        pve: fruit.pveRating,
        farming: fruit.farmingRating,
        raiding: fruit.raidingRating,
        value: fruit.value / 10000 // Scaled for chart visibility
      }));
    } else {
      const selectedWeapons = weapons.filter(weapon => 
        selectedItems.weapons.includes(weapon.id)
      );
      
      return selectedWeapons.map(weapon => ({
        name: weapon.name,
        damage: weapon.damage,
        speed: weapon.speed,
        range: weapon.range
      }));
    }
  };
  
  const comparisonData = getComparisonData();
  
  // Classes for consistent theming based on dark mode
  const cardClass = darkMode 
    ? 'bg-card-dark shadow-card-dark border border-gray-700' 
    : 'bg-white shadow-card-light';
    
  const headingClass = darkMode 
    ? 'text-gray-100' 
    : 'text-gray-900';
    
  const textClass = darkMode 
    ? 'text-gray-300' 
    : 'text-gray-600';
    
  const tableHeaderClass = darkMode 
    ? 'bg-gray-800 text-gray-200' 
    : 'bg-gray-50 text-gray-500';
    
  const tableBodyClass = darkMode 
    ? 'bg-gray-900/30 divide-y divide-gray-700' 
    : 'bg-white divide-y divide-gray-200';
    
  const tableCellClass = darkMode 
    ? 'border-gray-700' 
    : 'border-gray-200';

  const buttonActiveClass = darkMode
    ? 'bg-primary-dark text-white'
    : 'bg-primary-light text-white';
  
  const buttonInactiveClass = darkMode
    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
    : 'bg-gray-200 text-gray-700 hover:bg-gray-300';
    
  const selectionItemClass = darkMode
    ? 'border-gray-700 hover:bg-gray-800'
    : 'border-gray-300 hover:bg-gray-50';
    
  const selectionActiveClass = darkMode
    ? 'border-blue-500 bg-blue-900/30 text-blue-300'
    : 'border-blue-600 bg-blue-50 text-blue-800';
  
  return (
    <div>
      <h1 className={`text-3xl font-bold mb-6 ${headingClass}`}>Comparison Tools</h1>
      
      <div className={`p-6 rounded-lg transition-theme duration-300 ease-in-out mb-8 ${cardClass}`}>
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              comparisonType === 'fruits' ? buttonActiveClass : buttonInactiveClass
            }`}
            onClick={() => setComparisonType('fruits')}
          >
            Compare Fruits
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              comparisonType === 'weapons' ? buttonActiveClass : buttonInactiveClass
            }`}
            onClick={() => setComparisonType('weapons')}
          >
            Compare Weapons
          </button>
        </div>
        
        {comparisonType === 'fruits' ? (
          <>
            <h2 className={`text-xl font-bold mb-4 ${headingClass}`}>Select Fruits to Compare <span className="text-sm font-normal">(max 3)</span></h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
              {fruits.map(fruit => (
                <div 
                  key={fruit.id}
                  onClick={() => handleItemSelect(fruit.id, 'fruits')}
                  className={`p-3 border rounded-lg cursor-pointer text-center transition-all transform hover:-translate-y-1 ${
                    selectedItems.fruits.includes(fruit.id) 
                      ? selectionActiveClass
                      : selectionItemClass
                  }`}
                >
                  <div className={`font-medium ${headingClass}`}>{fruit.name}</div>
                  {selectedItems.fruits.includes(fruit.id) && (
                    <div className={`text-xs mt-1 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Selected</div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <h2 className={`text-xl font-bold mb-4 ${headingClass}`}>Select Weapons to Compare <span className="text-sm font-normal">(max 3)</span></h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
              {weapons.map(weapon => (
                <div 
                  key={weapon.id}
                  onClick={() => handleItemSelect(weapon.id, 'weapons')}
                  className={`p-3 border rounded-lg cursor-pointer text-center transition-all transform hover:-translate-y-1 ${
                    selectedItems.weapons.includes(weapon.id) 
                      ? selectionActiveClass 
                      : selectionItemClass
                  }`}
                >
                  <div className={`font-medium ${headingClass}`}>{weapon.name}</div>
                  {selectedItems.weapons.includes(weapon.id) && (
                    <div className={`text-xs mt-1 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>Selected</div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      
      {comparisonData.length > 0 ? (
        <div className={`p-6 rounded-lg transition-theme duration-300 ease-in-out ${cardClass}`}>
          <h2 className={`text-xl font-bold mb-6 ${headingClass}`}>Comparison Chart</h2>
          
          <ResponsiveContainer width="100%" height={400} className="mb-8">
            <BarChart
              data={comparisonData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis dataKey="name" stroke={darkMode ? '#9CA3AF' : '#4B5563'} />
              <YAxis stroke={darkMode ? '#9CA3AF' : '#4B5563'} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                  borderColor: darkMode ? '#4B5563' : '#E5E7EB',
                  color: darkMode ? '#F9FAFB' : '#111827'
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} />
              
              {comparisonType === 'fruits' ? (
                <>
                  <Bar dataKey="pvp" fill={darkMode ? '#8B5CF6' : '#8B5CF6'} name="PvP Rating" />
                  <Bar dataKey="pve" fill={darkMode ? '#10B981' : '#10B981'} name="PvE Rating" />
                  <Bar dataKey="farming" fill={darkMode ? '#F59E0B' : '#F59E0B'} name="Farming Rating" />
                  <Bar dataKey="raiding" fill={darkMode ? '#EF4444' : '#EF4444'} name="Raiding Rating" />
                  <Bar dataKey="value" fill={darkMode ? '#0EA5E9' : '#0EA5E9'} name="Value (10k Beli)" />
                </>
              ) : (
                <>
                  <Bar dataKey="damage" fill={darkMode ? '#EF4444' : '#EF4444'} name="Damage" />
                  <Bar dataKey="speed" fill={darkMode ? '#10B981' : '#10B981'} name="Attack Speed" />
                  <Bar dataKey="range" fill={darkMode ? '#F59E0B' : '#F59E0B'} name="Range" />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
          
          <h3 className={`font-semibold mb-4 ${headingClass}`}>Detailed Comparison</h3>
          <div className="overflow-x-auto rounded-lg">
            <table className="min-w-full border border-collapse">
              <thead className={tableHeaderClass}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r">Name</th>
                  {comparisonType === 'fruits' ? (
                    <>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r ${tableCellClass}`}>PvP</th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r ${tableCellClass}`}>PvE</th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r ${tableCellClass}`}>Farming</th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r ${tableCellClass}`}>Raiding</th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${tableCellClass}`}>Value</th>
                    </>
                  ) : (
                    <>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r ${tableCellClass}`}>Damage</th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b border-r ${tableCellClass}`}>Speed</th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b ${tableCellClass}`}>Range</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className={tableBodyClass}>
                {comparisonData.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? (darkMode ? 'bg-gray-800/50' : 'bg-gray-50') : ''}>
                    <td className={`px-6 py-4 whitespace-nowrap border-r ${tableCellClass} ${headingClass} font-medium`}>{item.name}</td>
                    {comparisonType === 'fruits' ? (
                      <>
                        <td className={`px-6 py-4 whitespace-nowrap border-r ${tableCellClass}`}>
                          <RatingBadge rating={item.pvp} darkMode={darkMode} /> 
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap border-r ${tableCellClass}`}>
                          <RatingBadge rating={item.pve} darkMode={darkMode} />
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap border-r ${tableCellClass}`}>
                          <RatingBadge rating={item.farming} darkMode={darkMode} />
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap border-r ${tableCellClass}`}>
                          <RatingBadge rating={item.raiding} darkMode={darkMode} />
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${textClass}`}>{(item.value * 10000).toLocaleString()} Beli</td>
                      </>
                    ) : (
                      <>
                        <td className={`px-6 py-4 whitespace-nowrap border-r ${tableCellClass} ${headingClass}`}>{item.damage}</td>
                        <td className={`px-6 py-4 whitespace-nowrap border-r ${tableCellClass} ${headingClass}`}>{item.speed}/10</td>
                        <td className={`px-6 py-4 whitespace-nowrap ${headingClass}`}>{item.range}/10</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className={`p-10 rounded-lg transition-theme duration-300 ease-in-out ${cardClass} text-center`}>
          <svg className={`mx-auto h-16 w-16 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className={`mt-4 text-lg ${textClass}`}>
            Select items to compare them
          </p>
          <p className={`mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            Choose up to three items to see detailed comparison
          </p>
        </div>
      )}
    </div>
  );
};

// Helper component for rating badges
const RatingBadge = ({ rating, darkMode }) => {
  let bgColor = "";
  let textColor = "";
  
  if (rating >= 9) {
    bgColor = darkMode ? 'bg-green-900/40' : 'bg-green-100';
    textColor = darkMode ? 'text-green-400' : 'text-green-800';
  } else if (rating >= 7) {
    bgColor = darkMode ? 'bg-blue-900/40' : 'bg-blue-100';
    textColor = darkMode ? 'text-blue-400' : 'text-blue-800';
  } else if (rating >= 5) {
    bgColor = darkMode ? 'bg-yellow-900/40' : 'bg-yellow-100';
    textColor = darkMode ? 'text-yellow-400' : 'text-yellow-800';
  } else {
    bgColor = darkMode ? 'bg-red-900/40' : 'bg-red-100';
    textColor = darkMode ? 'text-red-400' : 'text-red-800';
  }
  
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
      {rating}/10
    </span>
  );
};

export default ComparisonTools;
